import { isReasonCode, type ReasonCode } from '../../api';

import { isReasonParamCode, REASON_PARAMS, type ReasonParamCode } from './params';

/** i18n key of a reason text: `reason.<CODE>`. */
export type ReasonKey = `reason.${ReasonCode}`;

/** i18n key of a fallback text: `reasonFallback.<CODE>`. */
export type ReasonFallbackKey = `reasonFallback.${ReasonParamCode}`;

/** What a screen (or test) passes to i18next's `t` to show a reason. */
export interface ReasonMessage {
  readonly key: ReasonKey | ReasonFallbackKey;
  readonly values: Readonly<Record<string, string | number>>;
}

const GENERIC: ReasonMessage = { key: 'reason.INTERNAL_ERROR', values: {} };

// A parameter is shown only if it is a positive whole number: every declared parameter today
// is a count (REASON_PARAMS). Anything else would print nonsense ("abc min", "0 min", "NaN min")
// or carry data we did not plan to show, so the fallback text is used instead. When a code
// needs a text parameter, declare its kind in REASON_PARAMS and check it here.
function isDisplayableParam(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function pickParams(
  names: readonly string[],
  params: Readonly<Record<string, unknown>> | undefined,
): Record<string, string | number> | undefined {
  const values: Record<string, string | number> = {};
  for (const name of names) {
    const value = params?.[name];
    if (!isDisplayableParam(value)) {
      return undefined;
    }
    values[name] = value;
  }
  return values;
}

/**
 * Turns the `reasonCode` (and optional `params`) of an API problem or a bot event into the
 * i18n key and values to display.
 *
 * - Unknown or missing code → the generic `INTERNAL_ERROR` text (C2 spec R7).
 * - Parameter missing or invalid → the fallback text, so `{{…}}` is never shown (R8).
 * - Only parameters declared in `REASON_PARAMS` are kept (R6, I9).
 *
 * @param code - The `reasonCode` received, untrusted.
 * @param params - The parameters received with it, untrusted.
 * @returns The key and values to pass to `t`.
 * @example
 * const message = reasonMessage(problem.reasonCode, problem.params);
 * t(message.key, message.values);
 */
export function reasonMessage(
  code: string | undefined,
  params?: Readonly<Record<string, unknown>>,
): ReasonMessage {
  if (!isReasonCode(code)) {
    return GENERIC;
  }
  if (!isReasonParamCode(code)) {
    return { key: `reason.${code}`, values: {} };
  }
  const values = pickParams(REASON_PARAMS[code], params);
  return values === undefined
    ? { key: `reasonFallback.${code}`, values: {} }
    : { key: `reason.${code}`, values };
}
